import RequestHandler from './RequestHandler';
import { urlHttp, urlLocal, useLocalBackend, apiKeyP80 } from '../configs/appConfig.json';

/**
 * Cliente del backend AVAP.
 *
 * Dos destinos, seleccionados con `useLocalBackend` en appConfig.json:
 *
 *   true  -> AVAP Language Server local (contenedor avap-ls, puerto 8081).
 *            Sirve los 123 endpoints del repo, incluidos los que todavia no
 *            estan publicados en el gateway. Se llega por el proxy del
 *            dev-server (/avap-local) porque el contenedor no manda CORS.
 *   false -> Gateway de produccion (puerto 18000).
 *
 * No manda cabecera Authorization: el gateway la descarta y ningun .avap la
 * lee. Toda la autenticacion viaja como parametro en la query string.
 */
class ObexRequestHandlerP80 {
  private static getHeaders(disableCache = false) {
    return {
      '101obexapikey': apiKeyP80,
      'disable-cache': disableCache,
    };
  }


  private static readonly GATEWAY_ALIASES: Record<string, string> = {
    '/clients/list': '/client-list',
    '/clients/create': '/clients-create',
    '/clients/authorized-persons/list': '/clients-authorized-persons-list'
  };

  /**
   * El servidor local sirve la ruta tal y como la declara registerEndpoint
   * ("/auth/login-pin"), pero el gateway se come el primer segmento
   * ("/login-pin"). Verificado contra ambos: /auth/login -> /login,
   * /users/list -> /list, y /books_list se queda igual por no tener carpeta.
   *
   * Los servicios declaran SIEMPRE la ruta completa; aqui se aplana, salvo que
   * la ruta este en GATEWAY_ALIASES.
   */
  private static toGatewayPath(path: string): string {
    // Los servicios pegan el query string a la ruta antes de llamar, asi que
    // aqui llega "/clients/list?access_token=...". Hay que separarlo: si no, ni
    // la clave del alias coincide ni el aplanado cuenta bien los segmentos.
    const queryAt = path.indexOf('?');
    const route = queryAt === -1 ? path : path.slice(0, queryAt);
    const query = queryAt === -1 ? '' : path.slice(queryAt);

    const alias = ObexRequestHandlerP80.GATEWAY_ALIASES[route];
    if (alias) return `${alias}${query}`;

    const segments = route.replace(/^\//, '').split('/');
    if (segments.length < 2) return path;
    return `/${segments.slice(1).join('/')}${query}`;
  }

  private static getUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return useLocalBackend
      ? `${urlLocal}${cleanPath}`
      : `${urlHttp}${this.toGatewayPath(cleanPath)}`;
  }

  public static get(path: string, params?: object, disableCache = false): Promise<any> {
    return RequestHandler.get(this.getUrl(path), params, this.getHeaders(disableCache));
  }

  public static post(path: string, payload?: object, disableCache = false): Promise<any> {
    return RequestHandler.post(this.getUrl(path), payload, this.getHeaders(disableCache));
  }
}

export default ObexRequestHandlerP80;
